import { categorize } from './category';

/**
 * Which advertised offer covers a product — the same rules the server uses to price the cart (CouponService):
 * PRODUCTS / COLLECTIONS by id (the server lists a collection's products), BRANDS by vendor, TAGS by tag,
 * CATEGORIES by the exact product type or the derived category ("RING" covers "Womens Ring" and rings with no
 * type), ALL for everything. A badge is only a hint: the cart is always priced by the server.
 */

const up = (s) => String(s ?? '').trim().toUpperCase();
const tagList = (tags) => (Array.isArray(tags) ? tags : String(tags || '').split(','))
  .map((t) => String(t).trim()).filter(Boolean);

export const productPrice = (p) => Number(p?.marketPrice ?? p?.price ?? 0);

const inScope = (offer, product) => {
  const values = (offer.appliesToValues || []).map(up);
  switch (offer.appliesTo || 'ALL') {
    case 'ALL':
      return true;
    case 'PRODUCTS':
    case 'COLLECTIONS':
      return (offer.productIds || []).map(Number).includes(Number(product.id));
    case 'BRANDS':
      return values.includes(up(product.vendor));
    case 'TAGS':
      return tagList(product.tags).some((t) => values.includes(up(t)));
    case 'CATEGORIES': {
      const type = up(product.productType || product.type);
      if (type && values.includes(type)) return true;
      const key = up(categorize(product));
      return Boolean(key) && (values.includes(key) || values.includes(`${key}S`));
    }
    default:
      return false;
  }
};

/** True when the product can take part: as a paid item (≥ min price) or as the discounted one (≤ max price). */
export const offerCoversProduct = (offer, product) => {
  if (!offer || !product || !inScope(offer, product)) return false;
  const price = productPrice(product);
  const minBuy = offer.minBuyItemPrice != null ? Number(offer.minBuyItemPrice) : null;
  const maxGet = offer.maxGetItemPrice != null ? Number(offer.maxGetItemPrice) : null;
  const canBuy = minBuy == null || price >= minBuy;
  const canGet = maxGet == null || price <= maxGet;
  return canBuy || canGet;
};

const isFree = (offer) => Number(offer?.discountPercent ?? 100) >= 100;

/** Short label for a product card: "Buy 1 Get 1 Free" / "Buy 2 Get 1 · 50% off". */
export const offerBadgeText = (offer) => (offer
  ? `Buy ${offer.buyQuantity} Get ${offer.getQuantity}${isFree(offer) ? ' Free' : ` · ${Number(offer.discountPercent)}% off`}`
  : '');

const money = (n, sym) => `${sym}${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/** How it works, for the product page and banners. {@code sym} = the shopper's currency symbol. */
export const offerHowItWorks = (offer, sym = '₹') => {
  if (!offer) return '';
  const total = Number(offer.buyQuantity) + Number(offer.getQuantity);
  const y = Number(offer.getQuantity);
  const deal = isFree(offer) ? 'free' : `${Number(offer.discountPercent)}% off`;
  const which = y === 1 ? 'the lowest-priced one is' : `the ${y} lowest-priced are`;
  const parts = [`Add any ${total} items from this offer to your bag — ${which} ${deal}.`];
  if (offer.minBuyItemPrice != null) parts.push(`Paid items must be ${money(offer.minBuyItemPrice, sym)} or more.`);
  if (offer.maxGetItemPrice != null) {
    parts.push(`The ${isFree(offer) ? 'free' : 'discounted'} item can be up to ${money(offer.maxGetItemPrice, sym)}.`);
  }
  if (offer.maxApplications) {
    const n = offer.maxApplications * y;
    parts.push(`Up to ${n} ${isFree(offer) ? 'free' : 'discounted'} item${n === 1 ? '' : 's'} per order.`);
  }
  if (offer.paymentMode === 'PREPAID') parts.push('On online payment.');
  if (offer.paymentMode === 'COD') parts.push('On cash on delivery.');
  return parts.join(' ');
};
